import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  Paper,
  Fade,
  useTheme,
  Stack,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Import icons directly to avoid dynamic import warnings
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';
import InsightsIcon from '@mui/icons-material/Insights';
import AssignmentIcon from '@mui/icons-material/Assignment';

// Import responsive utilities
import { useResponsive } from '../theme/responsive';
import VirtualPATLogo from '../components/virtualpat-theme/VirtualPATLogo';

// Simplified motion variants for smooth animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

// Revolutionary hero section with dynamic visuals
function HeroSection() {
  const theme = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Track mouse for parallax effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };
    
    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [isMobile]);

  return (
    <Box
      sx={{
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        background: `linear-gradient(135deg, 
          ${theme.palette.mode === 'dark' ? '#0a0e27' : '#e3f2fd'} 0%, 
          ${theme.palette.mode === 'dark' ? '#1a237e' : '#bbdefb'} 50%,
          ${theme.palette.mode === 'dark' ? '#0d47a1' : '#90caf9'} 100%)`,
        overflow: 'hidden',
      }}
    >
      {/* Animated gradient orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}40 0%, transparent 70%)`,
          filter: 'blur(40px)',
          animation: 'float 20s ease-in-out infinite',
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
            '33%': { transform: 'translate(30px, -30px) scale(1.1)' },
            '66%': { transform: 'translate(-20px, 20px) scale(0.9)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.secondary.main}30 0%, transparent 70%)`,
          filter: 'blur(40px)',
          animation: 'float 25s ease-in-out infinite reverse',
          transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`,
        }}
      />

      {/* Dynamic workflow visualization */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ delay: 1, duration: 2 }}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '60%',
          height: '100%',
          display: { xs: 'none', md: 'block' },
          pointerEvents: 'none',
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 800 600"
          style={{ opacity: 0.7 }}
        >
          {/* Animated connection lines */}
          <motion.path
            d="M 100 100 Q 300 150 500 100"
            stroke={theme.palette.primary.main}
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          />
          <motion.path
            d="M 500 100 Q 650 200 700 350"
            stroke={theme.palette.secondary.main}
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 0.5, repeat: Infinity, repeatDelay: 3 }}
          />
          <motion.path
            d="M 100 100 Q 250 300 400 500"
            stroke={theme.palette.success.main}
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 1, repeat: Infinity, repeatDelay: 3 }}
          />
          
          {/* Animated nodes */}
          <motion.circle
            cx="100"
            cy="100"
            r="8"
            fill={theme.palette.primary.main}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.circle
            cx="500"
            cy="100"
            r="8"
            fill={theme.palette.secondary.main}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, delay: 0.5, repeat: Infinity }}
          />
          <motion.circle
            cx="700"
            cy="350"
            r="8"
            fill={theme.palette.success.main}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, delay: 1, repeat: Infinity }}
          />
          <motion.circle
            cx="400"
            cy="500"
            r="8"
            fill={theme.palette.info.main}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, delay: 1.5, repeat: Infinity }}
          />
        </svg>
      </Box>

      {/* Professional geometric patterns */}
      <Box sx={{ position: 'absolute', width: '100%', height: '100%', pointerEvents: 'none' }}>
        {!isMobile && (
          <>
            {/* Subtle cross patterns suggesting medical precision */}
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.05 }}
              transition={{ delay: 1.5, duration: 2 }}
              sx={{
                position: 'absolute',
                top: '20%',
                left: '15%',
                width: '60px',
                height: '60px',
              }}
            >
              <svg width="60" height="60" viewBox="0 0 60 60">
                <path
                  d="M30 0 L30 60 M0 30 L60 30"
                  stroke={theme.palette.primary.main}
                  strokeWidth="1"
                  opacity="0.3"
                />
                <circle cx="30" cy="30" r="8" fill="none" stroke={theme.palette.primary.main} strokeWidth="1" opacity="0.2" />
              </svg>
            </Box>
            
            {/* Hexagon patterns for tech/science feel */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 0.08, rotate: 360 }}
              transition={{ 
                opacity: { delay: 2, duration: 2 },
                rotate: { duration: 60, repeat: Infinity, ease: 'linear' }
              }}
              sx={{
                position: 'absolute',
                top: '60%',
                right: '20%',
                width: '80px',
                height: '80px',
              }}
            >
              <svg width="80" height="80" viewBox="0 0 80 80">
                <polygon
                  points="40,10 60,25 60,55 40,70 20,55 20,25"
                  fill="none"
                  stroke={theme.palette.secondary.main}
                  strokeWidth="1"
                  opacity="0.3"
                />
                <polygon
                  points="40,20 50,28 50,52 40,60 30,52 30,28"
                  fill="none"
                  stroke={theme.palette.secondary.main}
                  strokeWidth="1"
                  opacity="0.2"
                />
              </svg>
            </Box>
            
            {/* DNA helix suggestion */}
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.06 }}
              transition={{ delay: 2.5, duration: 2 }}
              sx={{
                position: 'absolute',
                bottom: '25%',
                left: '10%',
                width: '100px',
                height: '150px',
              }}
            >
              <svg width="100" height="150" viewBox="0 0 100 150">
                <path
                  d="M30 0 Q 70 30, 30 60 T 30 120"
                  fill="none"
                  stroke={theme.palette.info.main}
                  strokeWidth="2"
                  opacity="0.2"
                />
                <path
                  d="M70 0 Q 30 30, 70 60 T 70 120"
                  fill="none"
                  stroke={theme.palette.info.main}
                  strokeWidth="2"
                  opacity="0.2"
                />
              </svg>
            </Box>
          </>
        )}
      </Box>
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <Stack spacing={{ xs: 2, sm: 3, md: 3 }} alignItems="center" textAlign="center">
            {/* VirtualPAT Logo - Prominent placement */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6, type: 'spring' }}
            >
              <VirtualPATLogo 
                variant="full"
                size="large"
                height={isMobile ? 40 : 50}
                alt="VirtualPAT"
                sx={{
                  filter: theme.palette.mode === 'dark' 
                    ? 'drop-shadow(0 4px 20px rgba(255,255,255,0.1))' 
                    : 'drop-shadow(0 4px 20px rgba(0,0,0,0.1))',
                }}
              />
            </motion.div>

            {/* Animated tagline */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Chip
                label="AI-POWERED SURGICAL INTELLIGENCE"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
                  border: `1px solid ${theme.palette.primary.main}40`,
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  letterSpacing: 1,
                  px: 2,
                  py: 0.3,
                  fontSize: '0.7rem',
                }}
              />
            </motion.div>
            
            {/* Revolutionary headline with animated text */}
            <Box sx={{ position: 'relative' }}>
              <Typography
                component={motion.h1}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                sx={{
                  fontWeight: 900,
                  fontSize: { 
                    xs: '2rem', 
                    sm: '2.5rem', 
                    md: '3.5rem', 
                    lg: '4.5rem' 
                  },
                  lineHeight: 0.95,
                  color: theme.palette.mode === 'dark' ? 'white' : theme.palette.grey[900],
                  textShadow: theme.palette.mode === 'dark' 
                    ? '0 10px 40px rgba(0,0,0,0.5)' 
                    : '0 10px 40px rgba(0,0,0,0.1)',
                  mb: 1,
                }}
              >
                Intelligent
                <br />
                Pre-operative
                <br />
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(90deg, 
                      ${theme.palette.primary.main} 0%, 
                      ${theme.palette.secondary.main} 50%,
                      ${theme.palette.primary.main} 100%)`,
                    backgroundSize: '200% auto',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'gradient 3s linear infinite',
                    '@keyframes gradient': {
                      '0%': { backgroundPosition: '0% center' },
                      '100%': { backgroundPosition: '200% center' },
                    },
                  }}
                >
                  Assessment.
                </Box>
              </Typography>
              
              {/* Subheadline with typewriter effect */}
              <Typography
                component={motion.h2}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.125rem', sm: '1.5rem', md: '2rem' },
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              >
                Revolutionizing The Surgical Journey
              </Typography>
            </Box>

            {/* Enhanced description */}
            <Typography
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
              variant="h6"
              sx={{
                maxWidth: 700,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                lineHeight: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 400,
                mb: 2,
              }}
            >
              Revolutionize surgical readiness with AI-powered assessments that reduce delays, 
              enhance safety, and optimize outcomes.
            </Typography>

            {/* Interactive workflow visualization */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 800,
                p: { xs: 2, sm: 3 },
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                display: { xs: 'none', md: 'block' },
                mb: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                {[
                  { label: 'Patient Data', sublabel: 'EMR Integration', Icon: StorageIcon, color: theme.palette.primary.main },
                  { label: 'AI Analysis', sublabel: 'Risk Assessment', Icon: PsychologyIcon, color: theme.palette.secondary.main },
                  { label: 'Recommendations', sublabel: 'Clinical Insights', Icon: InsightsIcon, color: theme.palette.success.main },
                  { label: 'Protocols', sublabel: 'Evidence-Based', Icon: AssignmentIcon, color: theme.palette.info.main },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4 + index * 0.1 }}
                    style={{ flex: 1 }}
                  >
                    <Stack alignItems="center" spacing={1}>
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: 'spring', stiffness: 300 }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`,
                            border: `2px solid ${item.color}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: '-50%',
                              left: '-50%',
                              width: '200%',
                              height: '200%',
                              background: `linear-gradient(45deg, transparent, ${item.color}20, transparent)`,
                              animation: 'shimmer 3s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': { transform: 'translateX(-100%)' },
                              '100%': { transform: 'translateX(100%)' },
                            },
                          }}
                        >
                          <item.Icon 
                            sx={{ 
                              fontSize: 24, 
                              color: item.color,
                              zIndex: 1,
                            }} 
                          />
                        </Box>
                      </motion.div>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}>
                        {item.sublabel}
                      </Typography>
                    </Stack>
                  </motion.div>
                ))}
              </Stack>
            </Box>

            {/* CTA Buttons with enhanced styling */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              sx={{ mt: 1 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login?mode=hospital')}
                  sx={{
                    px: { xs: 4, sm: 5 },
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600,
                    borderRadius: 100,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}30`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 8px 25px ${theme.palette.primary.main}40`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  See It In Action
                </Button>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/request-demo')}
                  sx={{
                    px: { xs: 4, sm: 5 },
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600,
                    borderRadius: 100,
                    textTransform: 'none',
                    borderWidth: 2,
                    borderColor: theme.palette.mode === 'dark' ? 'white' : theme.palette.grey[900],
                    color: theme.palette.mode === 'dark' ? 'white' : theme.palette.grey[900],
                    backgroundColor: 'transparent',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      backgroundColor: `${theme.palette.primary.main}10`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Explore Platform
                </Button>
              </motion.div>
            </Stack>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

// Streamlined login options
function LoginOptions() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [hoveredCard, setHoveredCard] = useState(null);

  const options = [
    {
      id: 'hospital',
      title: 'Hospital',
      subtitle: 'Multi-facility health systems',
      icon: <LocalHospitalIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      path: '/login',
    },
    {
      id: 'asc',
      title: 'ASC',
      subtitle: 'Ambulatory Surgery Centers',
      icon: <MedicalServicesIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
      path: '/login',
    },
    {
      id: 'patient',
      title: 'Patient',
      subtitle: 'Access your health portal',
      icon: <PersonOutlineIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      path: '/login',
    },
  ];

  const handleOptionClick = (option) => {
    localStorage.setItem('appMode', option.id);
    navigate(option.path, { state: { mode: option.id } });
  };

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
              mb: 1,
            }}
          >
            Choose Your Access
          </Typography>
          
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, fontSize: { xs: '1rem', sm: '1.125rem' } }}
          >
            Purpose-built experiences for every healthcare setting
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            justifyContent="center"
            alignItems="stretch"
          >
            {options.map((option) => (
              <motion.div
                key={option.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{ flex: 1, display: 'flex' }}
              >
                <Card
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    border: `2px solid ${
                      hoveredCard === option.id 
                        ? option.color 
                        : theme.palette.divider
                    }`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                    },
                  }}
                  onMouseEnter={() => setHoveredCard(option.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => handleOptionClick(option)}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        bgcolor: `${option.color}15`,
                        color: option.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {option.icon}
                    </Box>
                    
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {option.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {option.subtitle}
                    </Typography>
                    
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        bgcolor: option.color,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.25,
                        '&:hover': {
                          bgcolor: option.color,
                          filter: 'brightness(0.9)',
                        },
                      }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      Sign In
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

// Clean feature highlights
function Features() {
  const theme = useTheme();
  const { isMobile } = useResponsive();

  const features = [
    {
      icon: <AutoGraphIcon />,
      title: 'AI Risk Assessment',
      description: 'Predictive analytics reduce surgical cancellations by 46%',
    },
    {
      icon: <SpeedIcon />,
      title: 'Real-time Collaboration',
      description: 'Instant notifications keep care teams synchronized',
    },
    {
      icon: <SecurityIcon />,
      title: 'Enterprise Security',
      description: 'HIPAA compliant with complete audit trails',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          alignItems="stretch"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              style={{ flex: 1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: `${theme.palette.primary.main}15`,
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

// Main HomePage Component - Clean and Modern
export default function HomePage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate initial load
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <VirtualPATLogo variant="icon" size="large" height={64} />
          <Typography variant="h6" color="text.secondary">Loading...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <AnimatePresence>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <HeroSection />
        <LoginOptions />
        <Features />
        
        {/* Simple footer */}
        <Box sx={{ py: 4, bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}` }}>
          <Container maxWidth="lg">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                © 2025 VirtualPAT. All rights reserved.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button size="small" color="inherit" sx={{ textTransform: 'none' }}>
                  Privacy
                </Button>
                <Button size="small" color="inherit" sx={{ textTransform: 'none' }}>
                  Terms
                </Button>
                <Button size="small" color="inherit" sx={{ textTransform: 'none' }}>
                  Contact
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>
    </AnimatePresence>
  );
}
